<template>
  <div>
    <v-expand-transition>
      <v-container fluid class="ma-0 pa-0">

        <!-- Advanced search chips -->
        <v-row class="mb-4">
          <v-col cols="12">
            <h5 class="search-header mb-2">Sökvillkor</h5>
            <div class="caption pb-4">Ange urval nedan. Datan kommer att filtreras utifrån det urval du anger. Om du anger start- och slutdatum filtreras datan utfrån det urvalet, men om du lämnar datumfälten tomma kommer aktuell data visas, t.ex pågående idag eller samtliga utfärdade till och med idag, osv.</div>
            <component class="mr-2 mb-2" @close="removeCriteria" v-for="(cmp,i) in displayValues" :didSearch="didSearch" :cmpData="cmp.data" :is="cmp.type" :key="i"></component>
          </v-col>
        </v-row>

        <!-- Advanced search settings -->
        <v-expand-transition>
          <v-row v-show="!didSearch">
            <v-col cols="12" md="6" v-for="(fld,i) in Object.keys(searchFields)" :key="i">

              <template v-if="!searchFields[fld].placeholder">

                <h6 class="criteriaLabel">{{ searchFields[fld].heading }}</h6>

                <template v-if="searchFields[fld].type === 'select'">
                  <v-select deletable-chips
                    :label="searchFields[fld].label"
                    :items="searchFields[fld].items"
                    @change="(v) => onChangeValue(v, fld)"
                    clearable
                    :value="value[fld]"
                    outlined
                    chips
                    :disabled="searchFields[fld].disabled ? true : false"
                    :hint="searchFields[fld].hint ? searchFields[fld].hint : ''"
                    :persistent-hint="searchFields[fld].hint ? true : false"
                    :multiple="searchFields[fld].multiple ? true : false"
                  ></v-select>
                </template>

                <template v-else-if="searchFields[fld].type === 'autocomplete'">
                  <v-autocomplete deletable-chips
                    :label="searchFields[fld].label"
                    :items="searchFields[fld].items"
                    @change="(v) => onChangeValue(v, fld)"
                    clearable
                    :value="value[fld]"
                    outlined 
                    chips  
                    :disabled="searchFields[fld].disabled ? true : false"
                    :hint="searchFields[fld].hint ? searchFields[fld].hint : ''"
                    :persistent-hint="searchFields[fld].hint ? true : false"
                    :multiple="searchFields[fld].multiple ? true : false"
                    :item-text="searchFields[fld].itemText"
                    :item-value="searchFields[fld].itemValue"
                    :filter="searchFields[fld].filter"
                  />
                </template>

                <template v-else-if="searchFields[fld].type === 'date'">
                  <DatePicker outlined clearable
                    :disabled="searchFields[fld].disabled ? true : false" 
                    :required="false" 
                    :label="searchFields[fld].label" 
                    @input="(v) => onChangeValue(v, fld)"
                    :value="value[fld]"
                  />
                </template>
                <template v-else-if="searchFields[fld].type === 'checkbox'">
                  <v-checkbox
                    :label="searchFields[fld].label || ''" 
                    :disabled="!!searchFields[fld].disabled"
                    v-model="value[fld]"
                    @change="(v) => onChangeValue(v, fld)"
                  />
                </template>

                <template v-else>
                  <v-text-field
                    :label="searchFields[fld].label"
                    @change="(v) => onChangeValue(v, fld)"
                    :value="value[fld]"
                    outlined
                    :disabled="searchFields[fld].disabled ? true : false"
                    :hint="searchFields[fld].hint ? searchFields[fld].hint : ''"
                    :persistent-hint="searchFields[fld].hint ? true : false"
                  ></v-text-field>
                </template>

              </template>
            </v-col>

          </v-row>
        </v-expand-transition>

        <!-- Buttons -->
        <v-container fluid class="ma-0 pa-0 d-flex">
          <v-btn v-if="!didSearch" :disabled="isLoading" @click="doSearch"><v-icon class="mr-2">$sl-search</v-icon>Sök</v-btn>
          <v-btn v-if="didSearch && showExportButton && !value.showPersonalData" :disabled="isLoading" class="mr-2" @click="exportResult"><v-icon class="mr-2">$sl-excel</v-icon>Exportera</v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="didSearch" :disabled="isLoading" class="mr-2" @click="didSearch=false"><v-icon class="mr-2">$sl-search-settings</v-icon>Ändra sökning</v-btn>
          <v-btn :disabled="isLoading" class="mr-2" outlined @click="resetForm">Nollställ sökning</v-btn>
          <v-btn v-if="!hideSwitch" :disabled="!allowSwitchMode || isLoading" outlined @click="didSearch=false; $emit('switchMode')">Enkel sökning</v-btn>
        </v-container>
      </v-container>
    </v-expand-transition>
  </div>
</template>

<script>
import DisplayChip from '@/components/search/DisplayChip'
import _find from 'lodash/find'
import _get from 'lodash/get'
import _filter from 'lodash/filter'
import _intersection from 'lodash/intersection'
import DatePicker from '@/components/common/DatePicker'
import { mapGetters } from 'vuex'

export default {
  props: {
    // The v-model value
    value: {
      type: Object,
    },
    searchFields: {
      type: Object,
      validator: v => Object.values(v).filter(d => !!d.type).every(fieldData => ['select', 'autocomplete', 'date', 'checkbox'].includes(fieldData.type))
    },
    initalSearch: {
      type: Boolean,
      default: false
    },
    allowSwitchMode: {
      type: Boolean,
      default: true
    },
    hideSwitch: {
      type: Boolean,
      default: false
    },
    showExportButton: {
      type: Boolean,
      default: false
    },
    emitSearchWhenReset: {
      type: Boolean,
      default: true
    },
    emitResetWhenReset: {
      type: Boolean,
      default: false
    }
  },

  components: {
    DisplayChip,
    DatePicker
  },

  data: () => ({
    didSearch: false
  }),

  created() {
    this.didSearch = this.initalSearch
  },

  computed: {
    ...mapGetters('app', ['isLoading']),

    displayValues() {
      const fields = Object.keys(this.value)
      return fields.map(k => { 
        let v = ""
        if (this.searchFields[k].items) {
          if (this.searchFields[k].multiple) {
            v = _filter(this.searchFields[k].items, (o) => {
              if (Array.isArray(o.value) && Array.isArray(this.value[k])) {
                return this.value[k] && _find(this.value[k], v => {
                  return _intersection(v, o.value).length === o.value.length
                })
              } else {
                return this.value[k] && this.value[k].includes(this.searchFields[k].itemValue ? o[this.searchFields[k].itemValue] : o.value)
              }
            }).map(i => this.searchFields[k].itemText ? i[this.searchFields[k].itemText] : i.text).join(', ')
          } else {
            const selItem = _find(this.searchFields[k].items, ['value', this.value[k]])
            if (selItem) {
              v = selItem.text
            }
          }
        } else {
          v = this.value[k]
        }
        return {
          type: (Array.isArray(this.value[k]) && this.value[k].length) ? 
            'DisplayChip' : 
            (!Array.isArray(this.value[k]) && this.value[k]) ? 'DisplayChip': null,
          data: {
            key: k, 
            heading: this.searchFields[k].heading,
            chipLabel: this.searchFields[k].chipLabel,
            closable: this.searchFields[k].disabled ? false : true,
            value: v
          }
        }
      })
    },
  },

  methods: {
    onChangeValue(v, fld){
       this.$emit('input', {
        ...this.value,
        [fld]: v
      })
    },

    resetForm() {
      this.removeAllCriterias()
      if (this.emitSearchWhenReset===true) {
        this.$emit('search')
      }
      if (this.emitResetWhenReset===true) {
        this.$emit('reset')
      }
      this.didSearch=false
    },

    doSearch() {
      this.didSearch=true
      this.$emit('search')
    },

    exportResult() {
      this.$emit('export')
    },

    removeCriteria(key) {
      if (this.searchFields[key] && this.searchFields[key].disabled || !this.didSearch){
        return
      }

      this.$emit('input', {
        ...this.value,
        [key]: null
      })

      this.$emit('search')
    },

    removeAllCriterias() {
      const fields = Object.keys(this.value)
      for (const f of fields) {
        if (this.searchFields[f] && this.searchFields[f].disabled) return
        this.value[f] = null
      }
      this.didSearch = true
    },
  }

}
</script>

<style scoped>
 h6.criteriaLabel {
   text-transform: uppercase;
 }
 h5.search-header {
   font-weight: normal !important;
   text-transform: none !important;
 }
</style>